import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import RevolutCheckout from '@revolut/checkout';
import { CreditCard, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Focused, standalone card-saving page. Opened in the mobile app's in-app
 * browser (same pattern as checkout) so the user lands directly on the card
 * form instead of the full Account Settings page. Authenticates via the
 * ?st=<session token>&u=<user> handoff that AppContext consumes on load, and
 * bounces back to the app's ?return=<deep link> on success.
 */
export default function SaveCard() {
  const { user } = useApp();
  const [token, setToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [cardholder, setCardholder] = useState('');
  const cardFieldRef = useRef(null);
  const cardInstanceRef = useRef(null);

  const returnUrl = (() => {
    try {
      return (
        new URLSearchParams(window.location.search).get('return') ||
        sessionStorage.getItem('cf_return') ||
        ''
      );
    } catch (e) {
      return '';
    }
  })();

  // Start the $1 Revolut setup order once the handoff user is available.
  useEffect(() => {
    if (!user?.email || token) return;
    let active = true;
    (async () => {
      try {
        const resp = await axios.post(`${API}/revolut/setup-card-order`, { user_email: user.email });
        if (!active) return;
        if (resp.data?.success) {
          setToken(resp.data.token);
          setOrderId(resp.data.order_id);
        } else {
          setError('Could not start card setup. Please try again.');
        }
      } catch (e) {
        if (active) setError(e.response?.data?.detail || 'Could not start card setup.');
      }
    })();
    return () => { active = false; };
  }, [user?.email, token]);

  // Mount the Revolut card field once we have a setup token.
  useEffect(() => {
    if (!token || done || !cardFieldRef.current) return;
    let mounted = true;
    (async () => {
      try {
        const instance = await RevolutCheckout(token);
        if (!mounted) return;
        const cardField = instance.createCardField({
          target: cardFieldRef.current,
          onSuccess: async () => {
            try {
              const r = await axios.post(`${API}/revolut/setup-card-complete`, {
                revolut_order_id: orderId,
                user_email: user.email,
              });
              if (r.data?.success) {
                setDone(true);
                if (returnUrl) setTimeout(() => { window.location.href = returnUrl; }, 1000);
              } else {
                setError(r.data?.message || 'Card could not be saved.');
              }
            } catch (e) {
              setError(e.response?.data?.detail || 'Card setup failed.');
            } finally {
              setSaving(false);
            }
          },
          onError: (err) => {
            setError(err?.message || 'Card setup failed.');
            setSaving(false);
          },
          styles: { default: { color: '#1f2937', fontSize: '15px', '::placeholder': { color: '#9ca3af' } } },
        });
        cardInstanceRef.current = cardField;
      } catch (err) {
        setError('Could not load card form. Please try again.');
      }
    })();
    return () => {
      mounted = false;
      if (cardInstanceRef.current?.destroy) cardInstanceRef.current.destroy();
      cardInstanceRef.current = null;
    };
  }, [token, done]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    if (!cardInstanceRef.current || saving) return;
    setSaving(true);
    setError('');
    cardInstanceRef.current.submit({
      name: cardholder.trim() || user?.firstName || 'Card Holder',
      email: user?.email || '',
      savePaymentMethodFor: 'merchant',
      billingAddress: { countryCode: 'AU', postcode: '2000' },
    });
    setTimeout(() => setSaving(false), 15000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center p-4" data-testid="save-card-page">
      <div className="bg-white dark:bg-neutral-900 rounded-2xl max-w-md w-full p-6 sm:p-8 shadow-lg border border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
            <CreditCard size={20} className="text-gray-700 dark:text-neutral-300" />
          </div>
          <h1 className="text-xl font-black text-gray-900 dark:text-neutral-100">Save a card</h1>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="save-card-done">
            <div className="w-14 h-14 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-1">Card saved</h2>
            <p className="text-sm text-gray-500 dark:text-neutral-400">
              {returnUrl ? 'Returning you to the app…' : 'You can close this window.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 dark:text-neutral-500 mb-4">
              A $1 AUD charge will be applied to verify the card and added to your account as
              credit. The card is saved for future top-ups and checkout.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-1">Cardholder name</label>
                <input
                  type="text"
                  value={cardholder}
                  onChange={(e) => setCardholder(e.target.value)}
                  placeholder={user?.firstName || 'Card holder'}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-sm focus:outline-none focus:border-black dark:focus:border-white"
                  data-testid="save-card-cardholder"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-neutral-400 mb-1">Card details</label>
                <div
                  ref={cardFieldRef}
                  className="min-h-[46px] px-3 py-2.5 rounded-lg border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950"
                  data-testid="save-card-field"
                />
                {!token && !error && (
                  <div className="flex items-center gap-2 text-gray-400 text-xs mt-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing secure card form…
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={saving || !token}
                className="w-full px-4 py-3 rounded-xl bg-black text-white font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 dark:border dark:border-white"
                data-testid="save-card-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save card ($1 credit)'}
              </button>
            </form>
          </>
        )}

        {error && (
          <div className="mt-4 flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl" data-testid="save-card-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}
